import { Clear } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { typingTimeOut } from '../../constants';
import { PaymentStatus } from '../../enums/paymentStatus';
import { QuickPaymentCategory } from '../../enums/quickPaymentCategory';
import { Talent } from '../../models/talent';
import { talentService } from '../../services/talentService';

export interface FilterPaymentsForm {
  talents?: Talent[];
  poNumber?: string;
  startDate?: string | null;
  endDate?: string | null;
  category?: string;
  address?: string;
  paymentStatuses?: string[];
}

const defaultValues: FilterPaymentsForm = {
  poNumber: '',
  talents: [],
  startDate: null,
  endDate: null,
  category: '',
  address: '',
  paymentStatuses: []
};

interface Props {
  handleFormSubmit: (data: FilterPaymentsForm) => void;
  filterData: FilterPaymentsForm;
}

const FilterPayments = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterPaymentsForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              id="talent"
              options={talents}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={filterData.talents ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  talents: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => (
                <TextField {...params} label="Payee Name" />
              )}
            />
            <DatePicker
              label="Start Date"
              onChange={(date) => {
                if (date)
                  setFilterData((prevState) => ({
                    ...prevState,
                    startDate: date
                  }));
              }}
              value={filterData.startDate}
              renderInput={(params) => (
                <TextField sx={{ maxWidth: 245 }} {...params} />
              )}
            />
            <DatePicker
              label="End Date"
              onChange={(date) => {
                if (date)
                  setFilterData((prevState) => ({
                    ...prevState,
                    endDate: date
                  }));
              }}
              value={filterData.endDate}
              renderInput={(params) => (
                <TextField sx={{ maxWidth: 245 }} {...params} />
              )}
            />
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="category">Category</InputLabel>
              <Select
                label="category"
                name="category"
                value={filterData.category}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Category" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.keys(QuickPaymentCategory).map((category) => (
                  <MenuItem
                    key={category}
                    value={
                      QuickPaymentCategory[
                        category as keyof typeof QuickPaymentCategory
                      ]
                    }
                  >
                    {
                      QuickPaymentCategory[
                        category as keyof typeof QuickPaymentCategory
                      ]
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ maxWidth: 245 }}
              label="Location"
              name="address"
              value={filterData.address}
              onChange={handleChange}
            />
            <Autocomplete
              multiple
              id="status"
              options={Object.keys(PaymentStatus)}
              getOptionLabel={(option) => {
                const key = option as keyof typeof PaymentStatus;
                return PaymentStatus[key];
              }}
              value={filterData.paymentStatuses ?? []}
              onChange={(event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  paymentStatuses: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Status" />}
            />
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterPayments;
